import { Icon } from "@oysterhr/ui"
import { useCallback, useEffect, useRef, useState } from "react"
import { useFileViewer } from "~/app/contexts/FileViewerContext"
import { DownloadLink, Maybe } from "@oysterhr/schema"

/** We need the legacy version to support our Node version (20.5).
 * We can use the newer version when we upgrade to Node 22.
 * In particular, Node v.20.5 doesn't support Promise.withResolvers.
 * Import path is created on vite.config.ts.
 * This module is mocked in Jest tests in frontend/packages/jest-setup/src/setup-pdfjs.js */
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf.mjs"
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist/types/src/pdf"
import { captureException } from "@sentry/browser"

/** pdf.js requires a "worker" path which is imported at runtime.
 * The file has been copied from pdfjs-dist/legacy/build to the public folder
 * (public/pdf.worker.min.js) to make it accessible from the browser. */
GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js"

type ControlsProps = {
  onPrevPage: () => void
  onNextPage: () => void
  pageNum: number
  pageCount: number
  controlsInCorner?: boolean
}
const Controls = ({
  onPrevPage,
  onNextPage,
  pageNum,
  pageCount,
  controlsInCorner,
}: ControlsProps) => {
  return (
    <div className="flex w-full justify-center">
      <div className={`absolute z-10 ${controlsInCorner ? "right-0 top-0" : "top-4"}`}>
        <div className={`rounded bg-gray-100 p-2 shadow ${controlsInCorner ? "mr-4" : ""}`}>
          <div className="flex">
            <div>
              <button id="prev" onClick={onPrevPage}>
                <Icon
                  name="ArrowCircleLeft"
                  className="hover:text-black-base text-gray-400"
                  size="md"
                />
              </button>
            </div>
            <div className="pl-2">
              <button id="next" onClick={onNextPage}>
                <Icon
                  name="ArrowCircleRight"
                  className="hover:text-black-base text-gray-400"
                  size="md"
                />
              </button>
            </div>
          </div>
          <div className="pt-3 text-center text-2xl font-bold text-gray-400">
            <span id="page_num">{pageNum}</span> / <span id="page_count">{pageCount}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function screenWidthToScale() {
  if (screen.width >= 1440) {
    return 1.2
  } else if (screen.width >= 1200) {
    return 1
  } else if (screen.width >= 1000) {
    return 0.8
  } else {
    return 0.6
  }
}

type PdfViewerProps = {
  file: Maybe<Partial<DownloadLink>> | undefined
  controlsInCorner?: boolean
}
export const PdfViewer = ({ file, controlsInCorner }: PdfViewerProps) => {
  const { loading, setLoading, error, setError } = useFileViewer()
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
  const [pageNum, setPageNum] = useState(1)
  const [pageCount, setPageCount] = useState<number>()
  const renderTaskRef = useRef<RenderTask | null>(null)

  useEffect(() => {
    let isMounted = true
    if (!file) {
      return
    }
    const documentSrc = file?.url || file?.base64
    if (!documentSrc) return

    getDocument(documentSrc)
      .promise.then(loadedDocument => {
        if (isMounted) {
          setPdfDoc(loadedDocument as unknown as PDFDocumentProxy)
          setPageCount(loadedDocument.numPages)
        }
      })
      .catch(e => {
        setError(e?.name ?? "error")
        captureException(e, { tags: { Component: "PDFViewer" } })
        setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [file, setError, setLoading])

  useEffect(() => {
    if (!pdfDoc) return
    const canvas = document.getElementById("the-canvas") as HTMLCanvasElement
    // canvas may be null in system specs, this line avoids flakiness.
    if (!canvas) return
    const canvasContext = canvas.getContext("2d") as CanvasRenderingContext2D

    // Cancel any ongoing renderTask
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel()
    }

    pdfDoc.getPage(pageNum).then(function (page) {
      const viewport = page.getViewport({ scale: screenWidthToScale() })

      canvas.height = viewport.height
      canvas.width = viewport.width

      // Render PDF page into canvas context
      const renderContext = {
        canvasContext,
        viewport,
      }

      const renderTask = page.render(renderContext)
      renderTaskRef.current = renderTask

      // Wait for rendering to finish
      renderTask.promise
        .then(function () {
          setLoading(false)
        })
        .catch(function (error) {
          // Ignore "Rendering cancelled" errors as they are expected (prevents specs failures)
          if (error?.name !== "RenderingCancelledException") {
            setError(error?.name ?? "error")
          }
          setLoading(false)
        })
    })

    // Cleanup function to cancel renderTask on unmount or dependencies change
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel()
      }
    }
  }, [pageNum, pdfDoc, setLoading, setError])

  const onPrevPage = useCallback(() => {
    if (pageNum <= 1) {
      return
    }
    setPageNum(pageNum - 1)
  }, [pageNum])

  const onNextPage = useCallback(() => {
    if (pageCount && pageNum + 1 <= pageCount) {
      setPageNum(pageNum + 1)
    }
  }, [pageNum, pageCount])

  if (error) {
    return null
  }

  return (
    <>
      {!loading && (
        <Controls
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          pageNum={pageNum}
          pageCount={pageCount || 0}
          controlsInCorner={controlsInCorner}
        />
      )}
      <div className="flex h-full items-center justify-center py-3">
        <div className="relative">
          <canvas id="the-canvas" className={loading ? "hidden" : ""}></canvas>
        </div>
      </div>
    </>
  )
}
